export const PE_FACTS_TOOL_ID = 'pe-facts';

export type PeFactsFlowStep = 'config' | 'loading' | 'result';

export type PeFactsTonality = 'sie' | 'du';

export type PeFactsStandardReason = {
  id: string;
  label: string;
  value: string;
};

export type PeFactsMasterclassModule = {
  id: string;
  num: string;
  title: string;
  gold?: boolean;
  paragraphs: string[];
  callout?: { title: string; body: string };
  highlight?: string;
};

export interface AcademyModuleData {
  id: string;
  number: string;
  badge: string;
  title: string;
  subtitle: string;
  coreQuote: string;
  takeaways: string[];
  actionStep: string;
}

export type PeFactsGeneratePayload = {
  targetDate: string;
  reasons: string[];
  tonality: PeFactsTonality;
  refineInstruction?: string;
};

/** Default-Tonalität für Generator-State und Requests */
export const PE_FACTS_DEFAULT_TONALITY: PeFactsTonality = 'sie';

export type PeFactsGenerateResult = {
  text: string;
};

export type PeFactsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  showNotification?: (msg: string, type?: 'success' | 'error') => void;
};

export const PE_FACTS_STANDARD_REASONS: PeFactsStandardReason[] = [
  {
    id: 'loehne',
    label: 'Faire Löhne & Existenzsicherung (Schutz vor Altersarmut)',
    value: 'Faire Löhne & Existenzsicherung (Schutz vor Altersarmut)',
  },
  {
    id: 'weiterbildung',
    label: 'Massive Investitionen in Team-Weiterbildung',
    value: 'Massive Investitionen in Team-Weiterbildung',
  },
  {
    id: 'produkte',
    label: 'Umstellung auf exklusive Premium-Produkte',
    value: 'Umstellung auf exklusive Premium-Produkte',
  },
  {
    id: 'interieur',
    label: 'Neues Interieur & Service-Upgrades',
    value: 'Neues Interieur & Service-Upgrades',
  },
  {
    id: 'auslastung',
    label: 'Mehr Fokus, weniger Stress (Reduzierte Auslastung)',
    value: 'Mehr Fokus, weniger Stress: Reduzierung der Auslastung für mehr Qualität',
  },
];

export const PE_FACTS_MASTERCLASS: PeFactsMasterclassModule[] = [
  {
    id: 'acc-1',
    num: '01',
    title: 'Das Fundament: Echte Kalkulation',
    paragraphs: [
      'Das größte Problem der Branche: Preise werden nicht kalkuliert, sie werden geschätzt. Die Folge? Die Preise passen überhaupt nicht zum tatsächlichen Aufwand.',
      'Du verlangst für Dienstleistung A viel zu wenig (und machst Verlust) und für Dienstleistung B verhältnismäßig zu viel. Genau hier entstehen die Diskussionen! Kunden sind nicht blöd. Sie regen sich nicht über Preise an sich auf, sondern über Preise, die für sie logisch nicht nachvollziehbar sind. Eine Preisanpassung muss auf einer sauberen, betriebswirtschaftlichen Kalkulation aufbauen.',
    ],
  },
  {
    id: 'acc-2',
    num: '02',
    title: 'Zielgruppe: Billig zieht Billig an',
    gold: true,
    paragraphs: [
      'Macht es wirklich Sinn, Kunden zu bedienen, die nur wegen des Preises kommen? Mit billigen Preisen ziehst du Schnäppchenjäger an. Diese Kunden sind oft unzufriedener, stellen höhere Ansprüche, sind anstrengend – wollen aber nichts bezahlen.',
      'Es gibt deutlich mehr billige und mittelpreisige Salons als hochpreisige. Dreh den Spieß um! Qualitätsbewusste Kunden erwarten gar keine billigen Preise. Sie zahlen gerne mehr, wenn die Qualität stimmt, sie kaufen Pflegeprodukte (Retail), geben mehr Trinkgeld und sind paradoxerweise leichter zufriedenzustellen als Pfennigfuchser.',
    ],
  },
  {
    id: 'acc-3',
    num: '03',
    title: 'Mindset & Die 20%-Rechnung',
    paragraphs: [
      'Dein größter Feind ist nicht der Kunde, es ist deine eigene Angst. Das Mindset muss sein: Ihr seid es wert! Warum seid ihr es wert? Wegen eurer Ausbildung, eurer Hingabe und eurer Qualität.',
    ],
    callout: {
      title: 'Die mathematische Wahrheit gegen die Angst:',
      body: 'Was passiert, wenn du deine Preise um 20% anpasst und im schlimmsten Fall 20% deiner Kunden verlierst? Nichts! Du hast exakt denselben Umsatz wie vorher. Aber du hast plötzlich 20% mehr Zeit! Zeit für bessere Arbeit, mehr Fokus auf den einzelnen Kunden, mehr Beratung und deutlich weniger Stress.',
    },
  },
  {
    id: 'acc-4',
    num: '04',
    title: 'Die Auslastungs-Falle (>75%)',
    gold: true,
    paragraphs: [
      'Eine extrem hohe Auslastung ist kein Grund zum Jubeln, es ist ein massives Problem! Jeder Wert über 75% Auslastung macht nachweislich krank (Eva-Prinzip) und schadet dem Unternehmen.',
      'Hohe Auslastung = Dauerstress. Es gibt keine Zeit für Zusatzdienstleistungen, keine Zeit für Beratung, keine Zeit für Retail-Verkauf. Schlimmer noch: Der Kunde spürt den Stress!',
    ],
    highlight:
      'Unser Auftrag ist es, dem Kunden eine geile, entspannte Auszeit zu liefern. Das ist völlig unmöglich, wenn der nächste Kunde schon böse um die Ecke guckt, weil sein Termin längst ran wäre.',
  },
  {
    id: 'acc-5',
    num: '05',
    title: 'Das Schutzschild: Der Aufsteller',
    paragraphs: [
      'Preisanpassungen werden niemals vorankündigt oder am Telefon ausdiskutiert. Und sie werden niemals direkt am Arbeitsplatz mit dem Kunden verhandelt – dort entstehen die gefährlichsten Diskussionen!',
      'Du informierst über einen Aufsteller. Dieser ist kein Deko-Element, er ist das Schutzschild für dein Team. Die Mitarbeiter dürfen keine Preisgespräche führen. Ihre Antwort lautet: „Bitte schauen Sie auf den Aufsteller. Das ist eine Entscheidung der Geschäftsführung – ich bin hier, um Sie glücklich zu machen. Bei Fragen wenden Sie sich bitte an die Inhaber.“',
    ],
  },
];

export const PE_FACTS_VIDEO_SRC = '/assets/pe-facts/preisanpassung.mp4';

/** Kurz-Intro (lokal) + volles Academy-Seminar (Firestore tutorialVideos) */
export const PE_FACTS_ACADEMY_SEMINAR_VIDEO_ID = '1789039244445';
export const PE_FACTS_ACADEMY_SEMINAR_FALLBACK_SRC =
  'https://vimeo.com/1225572827/9cb06ea389';
export const PE_FACTS_ACADEMY_SEMINAR_TITLE = 'Masterclass - Preisanpassung';
